import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import Logo from "../components/Logo";
import { homeFor } from "../lib/home";

const HERO = "https://static.prod-images.emergentagent.com/jobs/6791b343-2112-41a2-8ad4-0237b5984757/images/75b2e2bb08b9d82224a53725e1b23c1e26387e72ee8df3f0737cbe80e8d4c34c.png";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome back, ${u.name}`);
      const next = new URLSearchParams(location.search).get("next") || "";
      navigate(next.startsWith("/") && u.role === "client" ? next : homeFor(u), { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0A0A0A] text-white" data-testid="login-page">
      <div className="relative hidden lg:block">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A]/60 via-[#0A0A0A]/40 to-[#0A0A0A]" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-3" data-testid="login-brand">
            <Logo size={32} />
            <div>
              <div className="font-display text-2xl leading-none">TRAINA</div>
              <div className="tracker text-zinc-500 mt-1">Run your training business with confidence</div>
            </div>
          </Link>
          <div>
            <div className="tracker mb-4 text-brand">[ Coach Operating System ]</div>
            <h2 className="font-display text-5xl lg:text-6xl leading-[0.9] mb-4">
              <span className="text-brand">Every session.</span><br /><span className="text-brand">Every payment.</span><br /><span className="text-brand">All in one.</span>
            </h2>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-4 sm:p-6 lg:p-16">
        <div className="w-full max-w-md">
          <div className="tracker mb-4 text-brand">[ Authenticate ]</div>
          <h1 className="font-display text-4xl sm:text-5xl mb-2">Sign in.</h1>
          <p className="text-zinc-400 mb-10">Welcome back. Time to work.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="tracker block mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border bg-[#141414]"
                placeholder="you@gym.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="tracker block mb-2">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border bg-[#141414]"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="login-submit-button">
              {loading ? "Signing in…" : "Sign in"} <ArrowRight size={16} weight="bold" />
            </button>
          </form>

          <p className="mt-8 text-sm text-zinc-500 flex items-center justify-between flex-wrap gap-2">
            <span>New here? <Link to={location.search ? `/register${location.search}` : "/register"} className="text-brand hover:underline" data-testid="login-to-register">Create an account</Link></span>
            <Link to="/forgot-password" className="text-zinc-400 hover:text-white tracker" data-testid="login-to-forgot">Forgot password?</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
